import { useMemo } from "react";
import type { CctvCamera, CvCenter, CvDetectionEvent } from "@sikhio/shared";
import { useFeed } from "../hooks/useFeed";
import { ageLabel, cameraVerdict, centreSummary, classLabel, clampBbox, topDetection } from "../lib/cvCentre";

interface Props {
  apiBase: string;
  cameras: CctvCamera[];
  /** The per-camera state — lifted, so the wall and this sheet read one feed. */
  centres: CvCenter[];
  /** True while the centres feed is still in flight — the empty state must
   *  say "loading", not "nothing to report": those are different facts. */
  centresLoading?: boolean;
  onClose: () => void;
  onOpenCamera?: (cam: CctvCamera) => void;
}

/**
 * ศูนย์กล้องวิเคราะห์ภาพ · COMPUTER VISION CENTRE
 *
 * The surface a watch officer opens when the question is "what is the
 * camera seeing right now, and what changed?".
 *
 * WHAT IT SHOWS, AND WHY IT IS BUILT THIS WAY
 * --------------------------------------------
 * One cell per WATCHED camera. The cell is the picture — the actual JPEG
 * the probe grabbed from the NVR — with the detector's boxes drawn over
 * it, and underneath it the facts: what was detected, how confidently,
 * and when. A detection without the picture is a claim; the picture is
 * what lets the operator check it. So the picture is the primary content
 * of the cell, not a thumbnail beside a number.
 *
 * It is a MODAL, not a rail panel, because a rail panel would cost every
 * screen its pixels permanently for a surface that is used occasionally.
 * As a modal it costs nothing until asked for, and the camera wall on the
 * map carries the at-a-glance signal.
 *
 * IT DOES NOT SAY "PERSON DETECTED"
 * ---------------------------------
 * The detector measures change and presence; it does not classify what
 * moved. So the classes it emits are "motion" and "presence", and the
 * method line under the title says which model produced them. A panel
 * that claimed to know what moved would be making a claim the pipeline
 * never made.
 */

export function CctvCentre({ apiBase, cameras, centres, centresLoading, onClose, onOpenCamera }: Props) {
  // The event log is only read here, so it is fetched here — and only
  // while this sheet is on screen.
  const events = useFeed<CvDetectionEvent>(`${apiBase}/api/cctv/cv-events?limit=40`, 15_000);

  const names = useMemo(() => {
    const m = new Map<string, CctvCamera>();
    for (const c of cameras) m.set(c.id, c);
    return m;
  }, [cameras]);

  const now = Date.now();
  const cells = centres;
  const withDetection = events.data.filter((e) => e.detections.length > 0);
  const model = cells.find((c) => c.model)?.model ?? null;
  const summary = centreSummary(
    cells,
    events.fallbackTier === "loading" ? null : withDetection.length,
  );

  return (
    <div className="manual-backdrop cv-centre-backdrop" role="presentation">
      <section className="cv-centre" role="dialog" aria-modal="true" aria-label="ศูนย์กล้องวิเคราะห์ภาพ">
        <header className="cv-centre-head">
          <div className="col">
            <span className="eyebrow mono">SIKHIO · CCTV CENTRE</span>
            <h2 className="cv-centre-title">ศูนย์กล้องวิเคราะห์ภาพ</h2>
            <p className="cv-centre-sub mono">
              {cells.length > 0
                ? `${summary.cameras} กล้องที่เฝ้าระวัง · ${summary.withDetection} กล้องพบความเคลื่อนไหว · ${summary.events}`
                : centresLoading
                  ? "กำลังโหลดข้อมูลจากกล้อง…"
                  : "กำลังรอข้อมูลจากกล้อง…"}
              {model ? ` · ${model}` : ""}
            </p>
          </div>
          <button type="button" className="manual-close mono" onClick={onClose} aria-label="ปิดศูนย์กล้อง">
            ปิด
          </button>
        </header>

        <div className="cv-centre-body">
          {cells.length === 0 ? (
            <div className="cv-centre-empty">
              {centresLoading ? (
                <p>กำลังโหลดข้อมูลจากกล้อง…</p>
              ) : (
                <>
                  <p>ยังไม่มีกล้องรายงานผลการตรวจจับ</p>
                  <p className="cv-centre-hint">
                    ศูนย์กล้องรับข้อมูลจากโปรแกรมตรวจจับภาพที่ทำงานบนเครื่องผู้ดูแลระบบ
                    และรายงานเฉพาะกล้องที่เฝ้าระวังอยู่จริง — ไม่มีการเดาหรือเติมข้อมูลตัวอย่าง
                  </p>
                </>
              )}
            </div>
          ) : (
            <div className="cv-centre-grid">
              {cells.map((cell) => {
                const cam = names.get(cell.cameraId);
                const verdict = cameraVerdict(cell, now);
                return (
                  <article className="cv-cell" key={cell.cameraId}>
                    <div className="cv-cell-frame">
                      {cell.snapshotUrl ? (
                        <img
                          className="cv-cell-img"
                          src={`${apiBase}${cell.snapshotUrl}`}
                          alt={`ภาพกล้อง ${cam?.name ?? cell.cameraId}`}
                          loading="lazy"
                        />
                      ) : (
                        <div className="cv-cell-noimg" aria-hidden="true">
                          <span className="mono">ไม่มีภาพ</span>
                        </div>
                      )}
                      {/* The detector's boxes, drawn in normalised frame
                          coordinates so they land on the object at any size.
                          Clamped to the frame: a box that reached past the
                          picture's edge was drawn off the frame the
                          operator is reading — measured live, one box of
                          x=0.813 w=0.188 ended at 1.001 because the
                          detector rounded the origin and the size
                          separately. See clampBbox in lib/cvCentre.ts. */}
                      <svg className="cv-cell-boxes" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
                        {cell.detections.map((d, i) => {
                          if (!d.bbox) return null;
                          const b = clampBbox(d.bbox);
                          return (
                            <rect
                              key={i}
                              x={b.x * 100}
                              y={b.y * 100}
                              width={b.w * 100}
                              height={b.h * 100}
                              className={d.class === "motion" ? "cv-box cv-box--motion" : "cv-box cv-box--presence"}
                            />
                          );
                        })}
                      </svg>
                    </div>
                    <div className="cv-cell-meta">
                      <h3 className="cv-cell-name">{cam?.name ?? cell.cameraId}</h3>
                      <p className="cv-cell-state mono">
                        {verdict.state === "stale" ? (
                          <span className="cv-stale">ไม่ได้รายงาน · {verdict.age}</span>
                        ) : verdict.state === "detected" ? (
                          <>
                            <span className={`cv-tag cv-tag--${verdict.class}`}>{classLabel(verdict.class!)}</span>{" "}
                            {Math.round((verdict.confidence ?? 0) * 100)}% · {verdict.age}
                          </>
                        ) : (
                          <>ไม่พบการเคลื่อนไหว · {verdict.age}</>
                        )}
                      </p>
                    </div>
                    {cam && onOpenCamera && (
                      <button
                        type="button"
                        className="cv-cell-open mono"
                        onClick={() => onOpenCamera(cam)}
                        aria-label={`เปิดภาพสดของ ${cam.name}`}
                      >
                        ภาพสด ↗
                      </button>
                    )}
                  </article>
                );
              })}
            </div>
          )}

          <section className="cv-log" aria-label="บันทึกเหตุการณ์การตรวจจับ">
            <h3 className="cv-log-title">บันทึกเหตุการณ์ · EVENT LOG</h3>
            {withDetection.length === 0 ? (
              <p className="cv-log-empty mono">
                {events.fallbackTier === "loading"
                  ? "กำลังโหลดเหตุการณ์…"
                  : "ยังไม่มีเหตุการณ์การตรวจจับ"}
              </p>
            ) : (
              <ol className="cv-log-list">
                {withDetection.map((e) => {
                  const top = topDetection(e.detections);
                  const cam = names.get(e.cameraId);
                  return (
                    <li className="cv-log-row" key={`${e.cameraId}|${e.timestamp}`}>
                      <time className="cv-log-time mono" dateTime={e.timestamp}>
                        {new Date(e.timestamp).toLocaleTimeString("th-TH", {
                          hour: "2-digit",
                          minute: "2-digit",
                          second: "2-digit",
                        })}
                      </time>
                      <span className="cv-log-cam">{cam?.name ?? e.cameraId}</span>
                      {top && (
                        <span className={`cv-tag cv-tag--${top.class}`}>{classLabel(top.class)}</span>
                      )}
                      <span className="cv-log-conf mono">
                        {top ? `${Math.round((top.confidence ?? 0) * 100)}%` : "—"}
                      </span>
                      <span className="cv-log-where mono">{ageLabel(e.timestamp, now)}</span>
                    </li>
                  );
                })}
              </ol>
            )}
            <p className="cv-log-foot">
              ภาพในแต่ละช่องคือเฟรมล่าสุดของกล้องนั้น ณ เวลาที่เปิดศูนย์กล้อง
              — ไม่ใช่ภาพของเหตุการณ์ย้อนหลังแต่ละรายการ
            </p>
          </section>
        </div>
      </section>
    </div>
  );
}
